import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { CatalogService } from '@services/data/catalog.service';
import { ProcessDocRequirementAuditService } from '../../services/process-doc-requirement-audit.service';
import { formatAuditWhen } from '../../utils/doc-requirement-audit.utils';

@Component({
  selector: 'app-doc-process-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ListCardComponent, MobileFooterComponent],
  templateUrl: './process-list.component.html',
  styleUrls: ['./process-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DocProcessListComponent implements OnInit {
  readonly appSettings = inject(AppSettings);
  private readonly catalogService = inject(CatalogService);
  private readonly auditService = inject(ProcessDocRequirementAuditService);
  private readonly router = inject(Router);

  loading = signal(false);
  rows = signal<any[]>([]);
  searchText = '';
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly formatWhen = formatAuditWhen;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.catalogService.loadCatalogs();
    forkJoin({
      requirements: this.catalogService.loadDocumentRequirements().pipe(
        catchError(() => of(this.catalogService.documentRequirements() ?? []))
      ),
      audits: this.auditService.list({ limit: 500, offset: 0 }).pipe(
        catchError(() => of({ rows: [], total: 0 }))
      )
    }).subscribe({
      next: (res) => this.onLoadOk(res),
      error: () => this.onLoadFail()
    });
  }

  private onLoadOk(res: { requirements: any; audits: any }): void {
    const list = Array.isArray(res.requirements) ? res.requirements : [];
    this.catalogService.documentRequirements.set(list);
    const processes = this.catalogService.processes() ?? [];
    this.rows.set(this.mapProcessRows(processes, res.audits?.rows ?? []));
    this.loading.set(false);
    if (!processes.length) {
      setTimeout(() => this.retryMapIfReady(res.audits?.rows ?? []), 120);
    }
  }

  private retryMapIfReady(audits: any[]): void {
    const processes = this.catalogService.processes() ?? [];
    if (!processes.length) return;
    this.rows.set(this.mapProcessRows(processes, audits));
  }

  private onLoadFail(): void {
    this.rows.set([]);
    this.loading.set(false);
  }

  private mapProcessRows(processes: any[], audits: any[]): any[] {
    const latestBySlug = this.latestAuditBySlug(audits);
    const reqCountBySlug = this.reqCountBySlug();
    return (processes || [])
      .filter((p) => p.requiresDocuments === true)
      .map((p) => this.toProcessRow(p, latestBySlug, reqCountBySlug));
  }

  private reqCountBySlug(): Map<string, number> {
    const map = new Map<string, number>();
    for (const r of this.catalogService.documentRequirements() ?? []) {
      const slug = String(r.processSlug || '').toUpperCase();
      if (!slug) continue;
      map.set(slug, (map.get(slug) || 0) + 1);
    }
    return map;
  }

  private latestAuditBySlug(audits: any[]): Map<string, any> {
    const map = new Map<string, any>();
    for (const a of audits) {
      const slug = String(a.processSlug || '').toUpperCase();
      if (!slug || map.has(slug)) continue;
      map.set(slug, a);
    }
    return map;
  }

  private toProcessRow(
    process: any,
    latestBySlug: Map<string, any>,
    reqCountBySlug: Map<string, number>
  ): any {
    const slug = String(process.slug || '').toUpperCase();
    return {
      ...process,
      reqCount: reqCountBySlug.get(slug) || 0,
      lastAudit: latestBySlug.get(slug) || null
    };
  }

  filteredRows(): any[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter((p) => this.matchesSearch(p, q));
  }

  private matchesSearch(p: any, q: string): boolean {
    return String(p.name ?? '').toLowerCase().includes(q)
      || String(p.slug ?? '').toLowerCase().includes(q)
      || String(p.abbr ?? '').toLowerCase().includes(q);
  }

  viewProcess(process: any): void {
    const key = process?.slug || process?.uuid;
    if (!key) return;
    void this.router.navigate(['/configuracion/documentos', key]);
  }
}
